/**
 * RecentSearches - Shows recent property searches from localStorage
 * No email required - stored locally in browser
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Clock, X, MapPin, Bed, Home, Search,
  ChevronRight, Trash2
} from 'lucide-react';

const STORAGE_KEY = 'property_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// Get recent searches from localStorage
export const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save a search to recent searches
export const saveRecentSearch = (filters, resultCount = 0) => {
  try {
    const searches = getRecentSearches();
    
    // Create search entry
    const searchEntry = {
      id: Date.now().toString(),
      filters: { ...filters },
      resultCount,
      timestamp: new Date().toISOString(),
      displayText: formatFiltersForDisplay(filters)
    };
    
    // Check for duplicate (same filters)
    const isDuplicate = searches.some(s => 
      JSON.stringify(s.filters) === JSON.stringify(filters)
    );
    
    if (!isDuplicate) {
      // Add to beginning, limit to MAX_RECENT_SEARCHES
      const updated = [searchEntry, ...searches].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
    
    return searchEntry;
  } catch (e) {
    console.error('Error saving recent search:', e);
    return null;
  }
};

// Remove a search from recent searches
export const removeRecentSearch = (id) => {
  try {
    const searches = getRecentSearches();
    const updated = searches.filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
};

// Clear all recent searches
export const clearRecentSearches = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing recent searches:', e);
  }
};

// Format filters for display
const formatFiltersForDisplay = (filters, currency = 'Rs.') => {
  const parts = [];
  
  if (filters.location) {
    parts.push(filters.location);
  }
  
  if (filters.minPrice || filters.maxPrice) {
    const min = filters.minPrice ? `${currency}${(filters.minPrice / 1000).toFixed(0)}k` : 'Any';
    const max = filters.maxPrice ? `${currency}${(filters.maxPrice / 1000).toFixed(0)}k` : 'Any';
    parts.push(`${min} - ${max}`);
  }
  
  if (filters.bedrooms && filters.bedrooms !== 'any') {
    parts.push(filters.bedrooms === 'studio' ? 'Studio' : `${filters.bedrooms} bed`);
  }
  
  if (filters.propertyType && filters.propertyType !== 'any') {
    parts.push(filters.propertyType.replace('_', ' '));
  }
  
  return parts.length > 0 ? parts.join(' • ') : 'All properties';
};

// Build URL params from filters
const buildSearchUrl = (filters) => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value !== 'any') {
      params.set(key, value);
    }
  });
  
  const queryString = params.toString();
  return `/category/property${queryString ? `?${queryString}` : ''}`;
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

// Recent Searches Component
export default function RecentSearches({ 
  onSearchSelect,
  showClearAll = true,
  maxItems = 5,
  className = ''
}) {
  const [searches, setSearches] = useState([]);
  
  useEffect(() => {
    setSearches(getRecentSearches());
  }, []);
  
  const handleRemove = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = removeRecentSearch(id);
    setSearches(updated);
  };
  
  const handleClearAll = () => {
    clearRecentSearches();
    setSearches([]);
  };
  
  if (searches.length === 0) {
    return null;
  }
  
  return (
    <div className={`space-y-3 ${className}`} data-testid="recent-searches">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium">Recent Searches</span>
        </div>
        
        {showClearAll && searches.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-gray-500 hover:text-red-600 h-7"
            onClick={handleClearAll}
            data-testid="clear-recent-searches-btn"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Clear all
          </Button>
        )}
      </div>
      
      <div className="space-y-2">
        {searches.slice(0, maxItems).map((search) => (
          <Link 
            key={search.id}
            to={buildSearchUrl(search.filters)}
            onClick={() => onSearchSelect?.(search)}
            className="block"
          >
            <Card className="p-3 hover:bg-gray-50 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {search.displayText}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span>{formatRelativeTime(search.timestamp)}</span>
                    {search.resultCount > 0 && (
                      <>
                        <span>•</span>
                        <span>{search.resultCount} results</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleRemove(e, search.id)}
                    data-testid={`remove-search-${search.id}`}
                  >
                    <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                  </Button>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Compact version for inline display
export function RecentSearchesBadges({ onSearchSelect, maxItems = 3 }) {
  const [searches, setSearches] = useState([]);
  
  useEffect(() => {
    setSearches(getRecentSearches());
  }, []);
  
  if (searches.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2" data-testid="recent-searches-badges">
      <span className="text-xs text-gray-500 flex items-center">
        <Clock className="w-3 h-3 mr-1" />
        Recent:
      </span>
      {searches.slice(0, maxItems).map((search) => (
        <Link 
          key={search.id}
          to={buildSearchUrl(search.filters)}
          onClick={() => onSearchSelect?.(search)}
        >
          <Badge 
            variant="secondary" 
            className="cursor-pointer hover:bg-gray-200 text-xs"
          >
            {search.filters.location || 'All areas'}
            {search.filters.bedrooms && search.filters.bedrooms !== 'any' && 
              ` • ${search.filters.bedrooms} bed`}
          </Badge>
        </Link>
      ))}
    </div>
  );
}
